import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";

export interface RelativeTimeProps {
  value: string | number | Date | null | undefined;
  locale?: Intl.LocalesArgument;
  unit?: "second" | "minute" | "hour" | "day" | "week" | "month" | "year";
  numeric?: "always" | "auto";
}

const units: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
  ["second", 1],
];

export const RelativeTime = ({ value, locale, unit, numeric = "auto" }: RelativeTimeProps) => {
  const [now, setNow] = useState(Date.now);
  const timestamp = value instanceof Date ? value.getTime() : value == null ? NaN : new Date(value).getTime();
  const refreshInterval = Math.abs(timestamp - now) < 60_000 ? 1000 : 60_000;

  useEffect(() => {
    if (!Number.isFinite(timestamp)) return;
    const interval = setInterval(() => setNow(Date.now()), refreshInterval);
    return () => clearInterval(interval);
  }, [timestamp, refreshInterval]);

  if (!Number.isFinite(timestamp)) return <span>—</span>;

  const seconds = (timestamp - now) / 1000;
  const [resolvedUnit, divisor] = (unit ? units.find(([candidate]) => candidate === unit) : units.find(([, length]) => Math.abs(seconds) >= length)) ?? [
    "second",
    1,
  ];
  const relative = new Intl.RelativeTimeFormat(locale, { numeric }).format(Math.round(seconds / divisor), resolvedUnit);
  const absolute = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "long" }).format(timestamp);

  return (
    <Tooltip title={absolute}>
      <time dateTime={new Date(timestamp).toISOString()}>{relative}</time>
    </Tooltip>
  );
};
